import { useRef, useState } from "react";
import { videoFileSrc, mediaUrl } from "@/lib/media";
import { track } from "@/lib/tracking";
import { LOGO_URL, API } from "@/lib/api";
import { Play } from "lucide-react";

const MILESTONES = [[0.25, "VSL25Percent"], [0.5, "VSL50Percent"], [0.75, "VSL75Percent"]];

const Fallback = ({ fallbackPhotoId }) => (
  <div className="absolute inset-0 bg-black flex items-center justify-center" data-testid="vsl-fallback">
    {fallbackPhotoId ? (
      <img src={`${API}/photos/${fallbackPhotoId}`} alt="Furniture after ARK Revival" className="absolute inset-0 w-full h-full object-cover" />
    ) : (
      <div className="text-center px-8">
        <img src={LOGO_URL} alt="ARK Furniture" className="h-14 mx-auto mb-6 brightness-0 invert opacity-90" />
        <p className="text-[#c19a6b] text-[11px] uppercase tracking-[0.3em] font-bold">Furniture Revival</p>
      </div>
    )}
  </div>
);

// Square, user-controlled VSL. Never autoplays with sound; poster + big play button until the visitor taps.
export const VslPlayer = ({ vsl, posterId, fallbackPhotoId }) => {
  const videoRef = useRef();
  const fired = useRef(new Set());
  const [started, setStarted] = useState(false);
  const src = videoFileSrc(vsl);
  const poster = posterId ? mediaUrl(posterId) : undefined;

  const fire = (name) => {
    if (fired.current.has(name)) return;
    fired.current.add(name);
    track(name);
  };

  const play = () => {
    setStarted(true);
    videoRef.current?.play().catch(() => {});
  };

  const onTime = (e) => {
    const v = e.currentTarget;
    if (!v.duration) return;
    const pct = v.currentTime / v.duration;
    MILESTONES.forEach(([p, name]) => pct >= p && fire(name));
  };

  return (
    <div className="relative aspect-square w-full bg-black overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.18)]" data-testid="vsl-player">
      {src ? (
        <>
          <video
            ref={videoRef}
            src={src}
            poster={poster}
            controls={started}
            playsInline
            preload={poster ? "none" : "metadata"}
            onPlay={() => { setStarted(true); fire("VSLPlay"); }}
            onTimeUpdate={onTime}
            onEnded={() => fire("VSL75Percent")}
            className="absolute inset-0 w-full h-full object-cover bg-black"
          />
          {!started && (
            <button
              type="button"
              data-testid="vsl-play-btn"
              onClick={play}
              aria-label="Play video"
              className="absolute inset-0 flex flex-col items-center justify-center gap-5 bg-black/20 hover:bg-black/30 transition-colors duration-200"
            >
              <span className="w-20 h-20 rounded-full bg-white/95 flex items-center justify-center ring-2 ring-[#c19a6b]/70 shadow-2xl transition-transform duration-200 hover:scale-105">
                <Play size={30} className="text-black ml-1" fill="currentColor" />
              </span>
              <span className="text-white text-[11px] uppercase tracking-[0.28em] font-bold drop-shadow">Watch how Revival works</span>
            </button>
          )}
        </>
      ) : vsl?.embed_url ? (
        <iframe
          src={vsl.embed_url}
          title="ARK Furniture Revival"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
          data-testid="vsl-embed"
        />
      ) : poster ? (
        <img src={poster} alt="ARK Furniture Revival" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <Fallback fallbackPhotoId={fallbackPhotoId} />
      )}
    </div>
  );
};
